import React, { useState } from 'react';
import api from '../services/api';
import { API_ROUTES } from '../config/api';

interface FileUploadProps {
  onSuccess?: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onSuccess }) => {
    const [dbName, setDbName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const handleCreateEmpty = async () => {
        if (!dbName.trim()) {
            setError('Veuillez fournir un nom de base de données');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await api.post(
                API_ROUTES.DB_MANAGER.BASE + API_ROUTES.DB_MANAGER.CREATE_EMPTY,
                { db_name: dbName.trim() },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const message = response.data.name_warning 
                ? `${response.data.name_warning}. Base de données ${response.data.db_name} créée avec succès !`
                : `Base de données ${response.data.db_name} créée avec succès !`;
            
            setSuccess(message);
            setDbName('');
            
            onSuccess?.();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Une erreur est survenue');
            console.error('Erreur lors de la création:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        // Only create empty database - file upload removed from this flow
        await handleCreateEmpty();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Database Name Input */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de la base de données *
                </label>
                <input
                    type="text"
                    value={dbName}
                    onChange={(e) => setDbName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Entrez le nom de votre base de données"
                    required
                    disabled={loading}
                />
            </div>

            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    Créez une base de données vide. Vous pourrez ensuite importer vos articles depuis la page de gestion de la base de données.
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Success Message */}
            {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm">
                    {success}
                </div>
            )}

            {/* Submit Button */}
            <button
                type="submit"
                disabled={loading || !dbName.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
                {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                    <>
                        Créer la base de données
                    </>
                )}
            </button>
        </form>
    );
};

export default FileUpload;
